import {
    AI_CREATE_SESSION_SSE_EVENT,
    GetAICreateSessionResponse,
} from '@storefronts/common-types'
import { EventSource } from 'eventsource'

import { R } from './actions'

type SnapshotHandler = (response: GetAICreateSessionResponse) => void

/**
 * Subscribe to AI create session SSE via EventSource.
 * Returns an unsubscribe function that closes the connection.
 */
export function subscribeAICreateSessionEvents(
    sessionId: string,
    onSnapshot: SnapshotHandler,
    onError?: (event: Event) => void,
): () => void {
    const source = new EventSource(R.getAICreateSessionEventsUrl(sessionId), {
        fetch: (input, init) =>
            fetch(input, {
                ...init,
                headers: {
                    ...init?.headers,
                    Authorization: R.getBearerToken(),
                },
            }),
    })

    let closedIntentionally = false

    const handlePayload = (event: MessageEvent<string>) => {
        if (!event.data) {
            return
        }
        try {
            onSnapshot(JSON.parse(event.data) as GetAICreateSessionResponse)
        } catch (err) {
            console.error('Failed to parse AI create SSE payload:', err)
        }
    }

    const close = () => {
        closedIntentionally = true
        source.close()
    }

    source.addEventListener(
        AI_CREATE_SESSION_SSE_EVENT.SNAPSHOT,
        handlePayload as EventListener,
    )
    source.addEventListener(
        AI_CREATE_SESSION_SSE_EVENT.UPDATE,
        handlePayload as EventListener,
    )
    // Backend always emits `done` with the same terminal payload right after
    // snapshot/update — do not re-run completion handlers. Close so EventSource
    // does not reconnect after an intentional stream end.
    source.addEventListener(AI_CREATE_SESSION_SSE_EVENT.DONE, () => {
        close()
    })
    // EventSource also fires onerror during automatic reconnects; only
    // surface a hard close to callers.
    source.onerror = (event) => {
        if (closedIntentionally) {
            return
        }
        if (source.readyState === EventSource.CLOSED) {
            onError?.(event)
        }
    }

    return close
}

type AiCreateAgUiEventHandler = (event: unknown) => void

/**
 * Create-chat AG-UI SSE; reconnects re-hydrate server-side. Hard failures
 * (e.g. 404 from a stale backend) reach `onError` — callers must surface it.
 */
export function subscribeAICreateAgUiEvents(
    sessionId: string,
    onEvent: AiCreateAgUiEventHandler,
    onError?: (event: Event) => void,
): () => void {
    const source = new EventSource(
        R.getAICreateSessionAgUiEventsUrl(sessionId),
        {
            fetch: (input, init) =>
                fetch(input, {
                    ...init,
                    headers: {
                        ...init?.headers,
                        Authorization: R.getBearerToken(),
                    },
                }),
        },
    )

    let closedIntentionally = false

    const handlePayload = (event: MessageEvent<string>) => {
        if (!event.data) {
            return
        }
        try {
            onEvent(JSON.parse(event.data) as unknown)
        } catch (err) {
            console.error('Failed to parse AI create AG-UI SSE payload:', err)
        }
    }

    const close = () => {
        closedIntentionally = true
        source.close()
    }

    // AG-UI payloads are unnamed `message` frames; pings are a named `ping`
    // event and ignored for free.
    source.addEventListener('message', handlePayload as EventListener)
    // EventSource also fires onerror during automatic reconnects; only
    // surface a hard close to callers.
    source.onerror = (event) => {
        if (closedIntentionally) {
            return
        }
        if (source.readyState === EventSource.CLOSED) {
            onError?.(event)
        }
    }

    return close
}

type EditChatAgUiEventHandler = (event: unknown) => void

/**
 * Subscribe to edit-chat AG-UI SSE. Events arrive as default `message` frames
 * with AG-UI JSON in `data` (type field on the payload). Ping frames use a
 * named `ping` event and are ignored. Streams stay open across turns.
 */
export function subscribeEditChatAgUiEvents(
    landingPageHash: string,
    sessionId: string,
    onEvent: EditChatAgUiEventHandler,
    onError?: (event: Event) => void,
): () => void {
    const source = new EventSource(
        R.getEditChatAgUiEventsUrl(landingPageHash, sessionId),
        {
            fetch: (input, init) =>
                fetch(input, {
                    ...init,
                    headers: {
                        ...init?.headers,
                        Authorization: R.getBearerToken(),
                    },
                }),
        },
    )

    let closedIntentionally = false

    const handlePayload = (event: MessageEvent<string>) => {
        if (!event.data) {
            return
        }
        try {
            onEvent(JSON.parse(event.data) as unknown)
        } catch (err) {
            console.error('Failed to parse edit chat AG-UI SSE payload:', err)
        }
    }

    const close = () => {
        closedIntentionally = true
        source.close()
    }

    source.addEventListener('message', handlePayload as EventListener)
    source.onerror = (event) => {
        if (closedIntentionally) {
            return
        }
        if (source.readyState === EventSource.CLOSED) {
            onError?.(event)
        }
    }

    return close
}
